import { useEffect, useId, useMemo, useState } from 'react';
import { getChatLoadingPhase } from '../utils/chatLoadingExperience';
import './ChatLoadingExperience.css';

const LONG_WAIT_MS = 45000;
const ACTIVITY_MS = 3000;
const COLLAPSED_ROWS = 3;

const comparableText = (value) => value.normalize('NFKC').toLocaleLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, ' ').trim();

const SourceArtwork = ({ source }) => {
    const [failedURL, setFailedURL] = useState(null);
    const initials = source.name.split(/\s+/).slice(0, 2)
        .map((word) => Array.from(word)[0]).join('').toLocaleUpperCase();
    return (
        <span className='chatLoadingSourceArtwork' aria-hidden='true'>
            {source.imageURL && failedURL !== source.imageURL ? (
                <img src={source.imageURL} alt='' width='40' height='40'
                    onError={() => setFailedURL(source.imageURL)} />
            ) : initials}
        </span>
    );
};

const SourceGroup = ({ kind, items, count }) => {
    const [expanded, setExpanded] = useState(false);
    const id = useId();
    const isSpeaker = kind === 'speakers';
    const noun = isSpeaker ? 'speakers' : 'shows';
    const action = isSpeaker ? 'Considering' : 'Reviewing';
    const headingNoun = count === 1 ? (isSpeaker ? 'speaker' : 'show') : noun;
    const heading = count === 1 && items.length === 1 ? action
        : `${action} ${count === undefined ? '' : `${count.toLocaleString()} `}${headingNoun}`;
    const visibleItems = expanded ? items : items.slice(0, COLLAPSED_ROWS);
    const hasMore = items.length > COLLAPSED_ROWS;
    const isPartial = count > items.length;
    return (
        <section className={`chatLoadingSourceGroup chatLoadingSourceGroup-${kind}`} aria-labelledby={`${id}-heading`}>
            <h3 className='chatLoadingSourceHeading' id={`${id}-heading`}>
                <span className='chatLoadingSourceSpinner' aria-hidden='true' />
                {heading}
            </h3>
            {items.length > 0 && (
                <ul className={`chatLoadingSources chatLoadingSources-${kind}`} id={`${id}-list`}
                    aria-label={isSpeaker ? 'Speakers being considered' : 'Shows being considered'}>
                    {visibleItems.map((source) => (
                        <li className='chatLoadingSource' key={source.id}>
                            <SourceArtwork source={source} />
                            <span className='chatLoadingSourceCopy'>
                                <span className='chatLoadingSourceName'>{source.name}</span>
                                {source.subtitle && comparableText(source.subtitle) !== comparableText(source.name) && (
                                    <span className='chatLoadingSourceSubtitle'>{source.subtitle}</span>
                                )}
                            </span>
                        </li>
                    ))}
                </ul>
            )}
            {isPartial && (expanded || !hasMore) && items.length > 0 && (
                <div className='chatLoadingSourceLimit'>Showing {items.length.toLocaleString()} of {count.toLocaleString()}</div>
            )}
            {hasMore && (
                <button type='button' className='chatLoadingSourceToggle'
                    aria-controls={`${id}-list`} aria-expanded={expanded}
                    aria-label={expanded ? `Show less of the ${noun} list` : `Show more ${noun}`}
                    onClick={() => setExpanded((value) => !value)}>
                    {expanded ? 'Less' : 'More'}
                    <span className='chatLoadingSourceChevron' aria-hidden='true' />
                </button>
            )}
        </section>
    );
};

const LoadingActivity = ({ phase, understanding }) => {
    const [activityIndex, setActivityIndex] = useState(0);
    const [delayed, setDelayed] = useState(false);
    const isPlanning = phase.id === 'waiting' || phase.id === 'understanding';
    const sourceGroups = phase.sources ? [
        { kind: 'speakers', items: phase.sources.speakers, count: phase.sources.speakerCount },
        { kind: 'podcasts', items: phase.sources.podcasts, count: phase.sources.podcastCount },
    ].filter(({ items, count }) => items.length || count > 0) : [];
    const showSources = sourceGroups.length > 0;

    useEffect(() => {
        setDelayed(false);
        const timer = window.setTimeout(() => setDelayed(true), LONG_WAIT_MS);
        return () => window.clearTimeout(timer);
    }, [phase.id]);

    useEffect(() => setActivityIndex(0), [phase.activities]);

    useEffect(() => {
        if (delayed || !(phase.activities?.length > 1)) return undefined;
        const timer = window.setTimeout(() => setActivityIndex((value) => (value + 1) % phase.activities.length), ACTIVITY_MS);
        return () => window.clearTimeout(timer);
    }, [delayed, phase.activities, activityIndex]);

    let label = phase.activities?.[activityIndex % phase.activities.length] || phase.label;
    if (isPlanning && understanding) label = 'Understanding your request';
    if (delayed) label = 'Taking a little longer';

    return (
        <>
            <span className='chatLoadingAnnouncement chatLoadingDelayAnnouncement' role='status' aria-live='polite' aria-atomic='true'>
                {delayed ? 'Taking a little longer' : ''}
            </span>
            {(!showSources || delayed) && (
                <div className='chatLoadingActivity' key={label} aria-hidden='true'><span>{label}</span></div>
            )}
            {sourceGroups.map((group) => <SourceGroup key={group.kind} {...group} />)}
        </>
    );
};

export default function ChatLoadingExperience({ progress, useServerPlanningMessages = false, hasEarlyShowMatch = false }) {
    // Equivalent snapshots must not restart activity or source expansion.
    const phaseKey = JSON.stringify(getChatLoadingPhase(progress, { useServerPlanningMessages }));
    const phase = useMemo(() => JSON.parse(phaseKey), [phaseKey]);
    const message = hasEarlyShowMatch ? 'Checking for the best matches…' : phase.message;
    const isPlanning = phase.id === 'waiting' || phase.id === 'understanding';
    const [understanding, setUnderstanding] = useState(false);
    useEffect(() => {
        if (!isPlanning) return undefined;
        const timer = window.setTimeout(() => setUnderstanding(true), 2000);
        return () => window.clearTimeout(timer);
    }, [isPlanning]);

    return (
        <div className='chatLoadingStatus'>
            <span className='chatLoadingAnnouncement' role='status' aria-live='polite' aria-atomic='true'>
                {phase.label}. {message}
            </span>
            <p className='chatLoadingMessage' key={message} aria-hidden='true'>{message}</p>
            <LoadingActivity key={phase.id} phase={phase} understanding={understanding} />
        </div>
    );
}
